import { useEffect, useState, type ChangeEvent, type ReactNode, type SVGProps } from "react";
import { describeItem, FIELD_LABEL_FR, ITEM_KIND_FR, VAULT_PROBLEM_FR, VAULT_SOURCE_FR, VAULT_USE_FR, type FieldKind, type VaultItemView, type VaultUseView } from "../../shared/vault";
import { IconBank, IconCardPay, IconKey, IconLock, IconNote, IconPlus, IconUser, IconX } from "../components/icons";
import { Button, Card, CopyButton, cx, ErrorNotice, Field, Input, Loading, Notice, PageTop, ROUND_BUTTON, ROUND_BUTTON_PRIMARY } from "../components/ui";
import { VaultAddForm } from "../components/vault-form";
import { api, errorText } from "../lib/api";
import { channelLabel, fmtDateTime } from "../lib/format";
import { useElementWidth, useNow, usePageTitle } from "../lib/hooks";
import { useResource } from "../lib/store";

type Glyph = (p: SVGProps<SVGSVGElement>) => ReactNode;

const KIND_ICON: Record<VaultItemView["kind"], Glyph> = {
  login: IconKey,
  password: IconKey,
  card: IconCardPay,
  bank: IconBank,
  identity: IconUser,
  note: IconNote,
  other: IconLock,
};

/** « netflix.com · identifiant »: the site, and what kind of item it is. */
function subLine(item: VaultItemView): string {
  const site = item.website?.replace(/^https?:\/\//, "").replace(/\/$/, "");
  return [site, ITEM_KIND_FR[item.kind]?.toLowerCase()].filter(Boolean).join(" · ");
}

/** The masked value a row shows on the right (« •••• 4821 », « •••••••• »). */
function maskedOf(item: VaultItemView): string {
  const h = item.hints ?? {};
  return h.card_number ?? h.iban ?? h.password ?? h.otp ?? Object.values(h)[0] ?? "••••••••";
}

/**
 * /coffre — Pulsez's own vault (V2Coffre): one row per item, its values always masked; an item opens beside the list
 * (below it when narrow): its masked fields and « Voir » (a code sent to the account first, unless this session was
 * checked a moment ago; the values then show for 30 s, a ring counting down), its uses, « Supprimer ». « + » adds an
 * item through the form; « Importer » reads a password manager's CSV export.
 */
export function VaultPage() {
  usePageTitle("Coffre");
  const now = useNow(60_000);
  const [panel, setPanel] = useState<"add" | "import" | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const vault = useResource<VaultItemView[]>("vault", async (signal) => (await api("GET /api/vault", { signal })).items, { refreshOn: ["vault.changed"] });
  // The detail beside the list, or under the selected row when narrow.
  const [layoutRef, layoutWidth] = useElementWidth<HTMLDivElement>();
  const side = layoutWidth >= 700;
  const items = vault.data ?? [];
  const current = items.find((i) => i.n === selected) ?? null;
  const detail = current ? (
    <VaultDetail
      key={current.n}
      item={current}
      now={now}
      onClose={() => setSelected(null)}
      onDeleted={() => {
        setSelected(null);
        void vault.reload();
      }}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <PageTop
        title="Coffre"
        className="mb-0"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" aria-expanded={panel === "import"} onClick={() => setPanel(panel === "import" ? null : "import")} className="md:h-9">
              Importer
            </Button>
            <button type="button" aria-label="Ajouter" aria-expanded={panel === "add"} onClick={() => setPanel(panel === "add" ? null : "add")} className={panel === "add" ? ROUND_BUTTON : ROUND_BUTTON_PRIMARY}>
              {panel === "add" ? <IconX width={14} height={14} strokeWidth={2.6} /> : <IconPlus width={16} height={16} strokeWidth={2.6} />}
            </button>
          </div>
        }
      />
      {panel === "add" ? (
        <Card className="cv-in p-5">
          <h2 className="mb-4 text-[16px] font-semibold">Ajouter au coffre</h2>
          <VaultAddForm
            onSubmit={async (input) => {
              try {
                return await api("POST /api/vault", { body: input });
              } catch (err) {
                throw new Error(errorText(err));
              }
            }}
            onDone={() => void vault.reload()}
          />
        </Card>
      ) : null}
      {panel === "import" ? <ImportCard onDone={() => void vault.reload()} /> : null}

      {vault.loading ? (
        <Loading />
      ) : vault.error && !vault.data ? (
        <ErrorNotice message={vault.error.message} onRetry={() => void vault.reload()} />
      ) : !items.length ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-inset">
            <IconLock width={22} height={22} strokeWidth={1.9} />
          </span>
          <p className="font-display text-[22px]">Ton coffre est vide</p>
        </div>
      ) : (
        <div ref={layoutRef} className="flex gap-5">
          <ul className="flex min-w-0 flex-1 flex-col gap-0.5">
            {items.map((item) => {
              const on = item.n === selected;
              const Icon = KIND_ICON[item.kind] ?? IconLock;
              return (
                <li key={item.n} data-testid="vault-item">
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => setSelected(on ? null : item.n)}
                    className={cx("flex w-full min-w-0 items-center gap-3 rounded-[14px] px-3 py-2.5 text-left transition", on ? "bg-bubble-agent" : "hover:bg-elev")}
                  >
                    <span aria-hidden="true" className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[12px] bg-inset">
                      <Icon width={18} height={18} strokeWidth={1.9} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold">{item.status === "ready" ? item.label : `Lien 1Password « ${item.label} »`}</span>
                      <span className="mt-0.5 block truncate text-[12.5px] text-subtle">{subLine(item)}</span>
                    </span>
                    <span className="shrink-0 text-[14px] tracking-[0.06em] text-muted">{maskedOf(item)}</span>
                  </button>
                  {on && !side ? <div className="mt-1 mb-2">{detail}</div> : null}
                </li>
              );
            })}
          </ul>
          {side && detail ? <div className="w-[360px] shrink-0">{detail}</div> : null}
        </div>
      )}
      <p className="flex items-center gap-2 text-[12.5px] text-subtle">
        <IconLock width={14} height={14} strokeWidth={2} className="shrink-0" />
        Chiffré. Jamais montré à ton agent.
      </p>
    </div>
  );
}

function ImportCard({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setResult(null);
    if (file.size > 3_000_000) {
      setError("Fichier trop gros (3 Mo au plus).");
      return;
    }
    setBusy(true);
    try {
      const csv = await file.text();
      const r = await api("POST /api/vault/import", { body: { csv } });
      const extra = [r.duplicates ? `${r.duplicates} déjà présent${r.duplicates > 1 ? "s" : ""}` : "", r.skipped ? `${r.skipped} ignoré${r.skipped > 1 ? "s" : ""}` : ""].filter(Boolean).join(", ");
      const s = r.added > 1 ? "s" : "";
      setResult(`Export ${r.format} : ${r.added} élément${s} importé${s} et chiffré${s}${extra ? ` (${extra})` : ""}.`);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="cv-in space-y-3 p-5" data-testid="vault-import">
      <h2 className="text-[16px] font-semibold">Importer un fichier</h2>
      <p className="text-[13.5px] text-muted">CSV de 1Password, Bitwarden, Chrome, Edge ou Firefox. Chiffré aussitôt, jamais gardé.</p>
      <Field label="Fichier CSV" htmlFor="vault-import-file">
        <Input id="vault-import-file" type="file" accept=".csv,text/csv" disabled={busy} onChange={(e) => void onFile(e)} />
      </Field>
      {busy ? <Loading label="Import et chiffrement…" /> : null}
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {result ? (
        <Notice tone="success" role="status">
          {result}
        </Notice>
      ) : null}
    </Card>
  );
}

/** « Utilisé N fois, dernière utilisation … sur … ». */
function usesLine(item: VaultItemView, now: number): string | null {
  if (!item.uses) return null;
  const at = item.lastUse?.at ?? item.lastUsedAt;
  const when = at ? `, dernière utilisation ${fmtDateTime(at, now)}` : "";
  const where = item.lastUse?.site ? ` sur ${item.lastUse.site.replace(/^https?:\/\//, "")}` : "";
  return `Utilisé ${item.uses} fois${when}${where}`;
}


/** One item, opened: its masked fields and « Voir », its uses, « Supprimer ». */
function VaultDetail({ item, now, onClose, onDeleted }: { item: VaultItemView; now: number; onClose: () => void; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<VaultUseView[] | null>(null);
  const [reveal, setReveal] = useState(false);
  const Icon = KIND_ICON[item.kind] ?? IconLock;
  const hints = Object.entries(item.hints ?? {}) as [FieldKind, string][];
  const used = usesLine(item, now);

  useEffect(() => {
    if (!item.uses) return;
    let live = true;
    api("GET /api/vault/:n/historique", { params: { n: String(item.n) } })
      .then((r) => {
        if (live) setHistory(r.uses);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [item.n, item.uses]);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("DELETE /api/vault/:n", { params: { n: String(item.n) } });
      onDeleted();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <aside aria-label={item.label} data-testid="vault-detail" className="cv-in flex flex-col gap-4 rounded-[22px] bg-elev p-[22px]">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-inset">
          <Icon width={20} height={20} strokeWidth={1.9} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[22px] leading-tight">{item.label}</span>
          <span className="block text-[12.5px] text-subtle wrap-anywhere">{item.status === "ready" ? describeItem(item) : `Lien 1Password « ${item.label} »`}</span>
        </span>
        <button type="button" aria-label="Fermer" onClick={onClose} className={cx(ROUND_BUTTON, "bg-transparent text-subtle")}>
          <IconX width={13} height={13} strokeWidth={2.6} />
        </button>
      </div>

      <p className="flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-subtle">
        <span className={cx("font-semibold", item.status === "ready" ? "text-success" : item.status === "opening" ? "text-info" : "text-danger")}>
          {item.status === "ready" ? "Chiffré" : item.status === "opening" ? "Ouverture en cours…" : "Non ouvert"}
        </span>
        <span>· {VAULT_SOURCE_FR[item.source] ?? item.source}</span>
        {item.source === "link" || item.source === "paste" ? <span>· {channelLabel(item.channel)}</span> : null}
        <span>· {fmtDateTime(item.createdAt, now)}</span>
      </p>

      {item.status === "ready" ? (
        <div className="rounded-2xl bg-bg p-3.5">
          {reveal ? (
            <RevealPanel n={item.n} onClose={() => setReveal(false)} />
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0">
                {hints.length ? (
                  <span className="block text-[13.5px] text-muted wrap-anywhere" data-testid="vault-hints">
                    {hints.map(([k, v]) => `${FIELD_LABEL_FR[k]} ${v}`).join(" · ")}
                  </span>
                ) : (
                  <span className="block text-[13.5px] text-muted">{[...new Set(item.fields.map((f) => FIELD_LABEL_FR[f]))].join(", ")}</span>
                )}
              </span>
              <Button variant="secondary" size="sm" aria-label={`Révéler l'élément n°${item.n}`} onClick={() => setReveal(true)} className="md:h-8">
                Voir
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {item.status === "failed" && item.problem ? <p className="text-[12.5px] text-danger">{VAULT_PROBLEM_FR[item.problem]}</p> : null}
      {item.linkExpiresAt ? <p className="text-[12.5px] text-subtle">Lien valable jusqu'au {fmtDateTime(item.linkExpiresAt, now)}</p> : null}

      {item.status === "ready" ? (
        <div>
          <p className="mb-1.5 text-[12.5px] font-semibold text-subtle">Utilisé</p>
          <p className="text-[13.5px] text-muted" data-testid="vault-uses">
            {used ?? "Jamais utilisé pour l'instant"}
          </p>
          {history?.length ? (
            <ul className="mt-1 space-y-0.5 text-[12.5px] text-subtle" data-testid="vault-history">
              {history.slice(0, 6).map((u, i) => (
                <li key={`${u.at}-${i}`}>
                  {fmtDateTime(u.at, now)} — {VAULT_USE_FR[u.what] ?? u.what}
                  {u.site ? ` sur ${u.site.replace(/^https?:\/\//, "")}` : ""} par {u.by}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="text-[12.5px] text-danger">{error}</p> : null}
      {confirm ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" size="sm" disabled={busy} onClick={() => void remove()}>
            Supprimer définitivement
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirm(false)}>
            Annuler
          </Button>
        </div>
      ) : (
        <button
          type="button"
          aria-label={`Supprimer l'élément n°${item.n}`}
          onClick={() => setConfirm(true)}
          className="inline-flex min-h-8 items-center self-start text-[13.5px] text-subtle underline underline-offset-[3px] hover:text-fg max-md:min-h-11"
        >
          Supprimer
        </button>
      )}
    </aside>
  );
}

const SHOW_MS = 30_000;

/**
 * « Voir »: a code sent to the account first (unless this session was checked a moment ago) — the web has no Face ID,
 * the code is the check — then the item's values for 30 s, a ring counting down; « Copier », « Masquer ».
 */
function RevealPanel({ n, onClose }: { n: number; onClose: () => void }) {
  const [step, setStep] = useState<"checking" | "code" | "shown">("checking");
  const [masked, setMasked] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<{ kind: FieldKind; label: string; value: string }[]>([]);
  const [left, setLeft] = useState(SHOW_MS / 1000);

  const show = async () => {
    const r = await api("POST /api/vault/:n/reveler", { params: { n: String(n) }, body: {} });
    setFields(r.fields);
    setLeft(SHOW_MS / 1000);
    setStep("shown");
  };

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { until } = await api("GET /api/vault/reauth");
        if (!live) return;
        if (until) {
          await show();
          return;
        }
        const r = await api("POST /api/vault/reauth/start", { body: {} });
        if (!live) return;
        setMasked(r.masked);
        setStep("code");
      } catch (err) {
        if (live) {
          setError(errorText(err));
          setStep("code");
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);

  // Shown for 30 s, then masked again (the values leave the page).
  useEffect(() => {
    if (step !== "shown") return;
    const t = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) {
          clearInterval(t);
          setFields([]);
          onClose();
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [step, onClose]);

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await api("POST /api/vault/reauth/verify", { body: { code } });
      setCode("");
      await show();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const dash = (62.8 * (1 - left / (SHOW_MS / 1000))).toFixed(1);
  return (
    <div className="cv-in space-y-2.5" data-testid="vault-reveal">
      {step === "checking" ? <Loading label="Vérification…" /> : null}
      {step === "code" ? (
        <form
          className="flex flex-col gap-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <Field label={`Code envoyé à ${masked ?? "ton compte"}`} htmlFor={`reveal-code-${n}`}>
            <Input
              id={`reveal-code-${n}`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              placeholder="••••••"
              autoFocus
              className="h-11 bg-bubble-agent text-center text-[20px] tracking-[0.5em]"
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" busy={busy} disabled={busy || code.replace(/\s+/g, "").length !== 6} className="flex-1 md:h-9">
              Valider
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={onClose} className="md:h-9">
              Annuler
            </Button>
          </div>
        </form>
      ) : null}
      {step === "shown" ? (
        <div className="space-y-2">
          <ul className="space-y-1.5">
            {fields.map((f, i) => (
              <li key={`${f.kind}-${i}`} className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-subtle">{f.label || FIELD_LABEL_FR[f.kind]}</span>
                <code className="min-w-0 font-mono text-[15px] break-all text-fg" data-testid="vault-revealed-value">
                  {f.value}
                </code>
                <CopyButton text={f.value} className="ml-auto md:h-8" />
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2">
            <svg width="26" height="26" viewBox="0 0 26 26" role="img" aria-label={`${left} secondes`}>
              <circle cx="13" cy="13" r="10" fill="none" stroke="var(--chip)" strokeWidth="3" />
              <circle
                cx="13"
                cy="13"
                r="10"
                fill="none"
                stroke="var(--accent)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="62.8"
                strokeDashoffset={dash}
                transform="rotate(-90 13 13)"
                style={{ transition: "stroke-dashoffset 1s linear" }}
              />
            </svg>
            <Button size="sm" variant="ghost" onClick={onClose} className="md:h-8">
              Masquer
            </Button>
          </div>
        </div>
      ) : null}
      {error ? <p className="text-[12.5px] text-danger">{error}</p> : null}
    </div>
  );
}
